import React, { Suspense, lazy } from 'react';
import PopupLoader from '@components/common/PopupLoader/PopupLoader';
import { usePopupAutoOpen } from '@hooks/popups/usePopupAutoOpen';
import { usePopupHostApi } from '@hooks/popups/usePopupHostApi';
import { PopupDataMap, PopupName, usePopups } from '@hooks/popups/usePopups';
import { retryImport } from '@utils/retryImport';

type PopupProps<K extends PopupName> = {
  data: PopupDataMap[K];
  onClose: () => void;
};

type PopupComponents = {
  [K in PopupName]: React.FC<PopupProps<K>>;
};

// Loaded on first open, so the paywall and its images stay out of the main
// bundle — of the SPA and of the island alike (an `island.*.chunk.js`).
const LazySubscriptionPopup = lazy(() =>
  retryImport(
    () =>
      import(
        /* webpackChunkName: "subscription-popup" */ '@components/popups/SubscriptionPopup/SubscriptionPopup'
      )
  )
);

const SubscriptionPopup: React.FC<PopupProps<'SubscriptionPopup'>> = (
  props
) => (
  <Suspense fallback={<PopupLoader />}>
    <LazySubscriptionPopup {...props} />
  </Suspense>
);

// Same reasoning: the celebration's video URLs and confetti stay out of the
// main bundle until something opens it. It has no data to wait for, so there is
// nothing to show while the chunk lands.
const LazyTrialMonthlyRebillPopup = lazy(() =>
  retryImport(
    () =>
      import(
        /* webpackChunkName: "trial-monthly-rebill-popup" */ '@components/popups/TrialMonthlyRebillPopup/TrialMonthlyRebillPopup'
      )
  )
);

const TrialMonthlyRebillPopup: React.FC<
  PopupProps<'TrialMonthlyRebillPopup'>
> = (props) => (
  <Suspense fallback={null}>
    <LazyTrialMonthlyRebillPopup {...props} />
  </Suspense>
);

// The offer waits for its prices, so it shows the paywall's loader meanwhile.
const LazyRescuePopup = lazy(() =>
  retryImport(
    () =>
      import(
        /* webpackChunkName: "rescue-popup" */ '@components/popups/RescuePopup/RescuePopup'
      )
  )
);

const RescuePopup: React.FC<PopupProps<'RescuePopup'>> = (props) => (
  <Suspense fallback={<PopupLoader />}>
    <LazyRescuePopup {...props} />
  </Suspense>
);

// The offer waits for its price as well.
const LazyAnnualPlanPopup = lazy(() =>
  retryImport(
    () =>
      import(
        /* webpackChunkName: "annual-plan-popup" */ '@components/popups/AnnualPlanPopup/AnnualPlanPopup'
      )
  )
);

const AnnualPlanPopup: React.FC<PopupProps<'AnnualPlanPopup'>> = (props) => (
  <Suspense fallback={<PopupLoader />}>
    <LazyAnnualPlanPopup {...props} />
  </Suspense>
);

// The words popups wait for their prices as well.
const LazyWordsPricesPopup = lazy(() =>
  retryImport(
    () =>
      import(
        /* webpackChunkName: "words-prices-popup" */ '@components/popups/WordsPricesPopup/WordsPricesPopup'
      )
  )
);

const WordsPricesPopup: React.FC<PopupProps<'WordsPricesPopup'>> = (props) => (
  <Suspense fallback={<PopupLoader />}>
    <LazyWordsPricesPopup {...props} />
  </Suspense>
);

const LazyPayAsYouGoPopup = lazy(() =>
  retryImport(
    () =>
      import(
        /* webpackChunkName: "pay-as-you-go-popup" */ '@components/popups/PayAsYouGoPopup/PayAsYouGoPopup'
      )
  )
);

const PayAsYouGoPopup: React.FC<PopupProps<'PayAsYouGoPopup'>> = (props) => (
  <Suspense fallback={<PopupLoader />}>
    <LazyPayAsYouGoPopup {...props} />
  </Suspense>
);

const POPUP_COMPONENTS: PopupComponents = {
  SubscriptionPopup,
  TrialMonthlyRebillPopup,
  RescuePopup,
  AnnualPlanPopup,
  WordsPricesPopup,
  PayAsYouGoPopup,
};

const POPUP_NAMES = Object.keys(POPUP_COMPONENTS) as PopupName[];

type PopupsProps = {
  names?: PopupName[];
};

/**
 * Renders the popups opened with `usePopups().openPopup`. Mount once per root.
 * The host page reaches the popups this root renders through `AiTools.Popups`
 * (see `usePopupHostApi`) and opens them from the URL (see `usePopupAutoOpen`).
 */
const Popups: React.FC<PopupsProps> = ({ names = POPUP_NAMES }) => {
  const { popups, closePopup } = usePopups();

  usePopupHostApi({ names });
  usePopupAutoOpen({ names });

  return (
    <>
      {names.map((name) => {
        const popup = popups[name];

        if (!popup?.isOpen) {
          return null;
        }

        const PopupComponent = POPUP_COMPONENTS[name];

        return (
          <PopupComponent
            key={name}
            // The payload is the one `PopupDataMap` gives this name; over the
            // union of names TS intersects the popups' props instead, and two
            // `variant` unions of different popups meet as `never`.
            data={(popup.data ?? {}) as never}
            onClose={() => closePopup(name)}
          />
        );
      })}
    </>
  );
};

export default Popups;
